import { FORMAT_EXTENSIONS, FORMAT_LABELS, FORMAT_MIME } from './conversions';
import { SITE_NAME, SITE_URL } from './site';
import { ImageFormat } from '../types/media';

interface FormatSeoMeta {
  noun: string;
  strengths: string[];
  commonUses: string[];
  watchOut: string;
  conversionNote: string;
}

export interface SeoContentItem {
  title: string;
  body: string;
}

export interface ImageSeoContent {
  conversionName: string;
  howItWorks: SeoContentItem[];
  examples: SeoContentItem[];
  benefits: SeoContentItem[];
  troubleshooting: SeoContentItem[];
  faqs: SeoContentItem[];
}

const FORMAT_SEO_META: Record<ImageFormat, FormatSeoMeta> = {
  jpeg: {
    noun: 'compressed photo',
    strengths: ['small file sizes', 'broad camera and web support', 'fast sharing'],
    commonUses: ['product photos', 'social posts', 'email attachments'],
    watchOut: 'JPEG does not preserve transparent backgrounds, so transparent pixels may flatten.',
    conversionNote: 'best for photos and realistic artwork where a compact download matters',
  },
  png: {
    noun: 'lossless image',
    strengths: ['transparent backgrounds', 'crisp edges', 'lossless quality'],
    commonUses: ['logos', 'screenshots', 'UI graphics'],
    watchOut: 'PNG files can be larger than photo-focused formats.',
    conversionNote:
      'ideal when sharp detail or transparency is more important than the smallest file',
  },
  webp: {
    noun: 'modern web image',
    strengths: ['excellent compression', 'web performance', 'quality at smaller sizes'],
    commonUses: ['landing pages', 'blogs', 'image-heavy websites'],
    watchOut: 'Some older tools may not open WebP without an update or plugin.',
    conversionNote: 'a strong choice for faster pages and lighter image delivery',
  },
  svg: {
    noun: 'vector graphic',
    strengths: ['scalable artwork', 'tiny icon files', 'editable paths'],
    commonUses: ['icons', 'simple illustrations', 'brand marks'],
    watchOut: 'Converting SVG to a raster format fixes the output at a pixel size.',
    conversionNote: 'useful when a vector file needs to work in apps that expect a bitmap image',
  },
  gif: {
    noun: 'simple web graphic',
    strengths: ['basic animation support', 'wide compatibility', 'small color palettes'],
    commonUses: ['simple animations', 'badges', 'legacy web assets'],
    watchOut: 'GIF supports limited colors, so gradients and photos can look banded.',
    conversionNote: 'best for simple graphics that need maximum compatibility',
  },
  bmp: {
    noun: 'bitmap image',
    strengths: ['uncompressed detail', 'legacy Windows support', 'simple pixel data'],
    commonUses: ['legacy apps', 'print workflows', 'archival transfers'],
    watchOut: 'BMP files are usually much larger than web formats.',
    conversionNote: 'helpful when older software requires a straightforward bitmap file',
  },
  tiff: {
    noun: 'high-detail image',
    strengths: ['print workflows', 'archival quality', 'professional imaging'],
    commonUses: ['scans', 'publishing', 'design handoffs'],
    watchOut: 'TIFF files can be large and are not always ideal for browsers.',
    conversionNote: 'suited to professional or archival workflows where quality is the priority',
  },
  ico: {
    noun: 'icon file',
    strengths: ['favicon support', 'app icons', 'Windows compatibility'],
    commonUses: ['website favicons', 'desktop shortcuts', 'app branding'],
    watchOut: 'ICO is specialized for icons, not general photo sharing.',
    conversionNote: 'made for favicon and icon workflows where .ico output is required',
  },
};

function label(format: ImageFormat): string {
  return FORMAT_LABELS[format];
}

function extensions(format: ImageFormat): string {
  return FORMAT_EXTENSIONS[format].map((ext) => `.${ext}`).join(', ');
}

function firstUse(format: ImageFormat): string {
  return FORMAT_SEO_META[format].commonUses[0];
}

export function getImageSeoContent(from: ImageFormat, to: ImageFormat): ImageSeoContent {
  const fromLabel = label(from);
  const toLabel = label(to);
  const conversionName = `${fromLabel} to ${toLabel}`;
  const fromMeta = FORMAT_SEO_META[from];
  const toMeta = FORMAT_SEO_META[to];
  const fromExtensions = extensions(from);
  const toExtensions = extensions(to);

  return {
    conversionName,
    howItWorks: [
      {
        title: `Upload a ${fromLabel} file`,
        body: `Choose a ${fromMeta.noun} from your device. This ${conversionName} converter accepts ${fromExtensions} files with the ${FORMAT_MIME[from]} media type when your browser provides it.`,
      },
      {
        title: `Review the ${conversionName} direction`,
        body: `The page is locked to ${fromLabel} input and ${toLabel} output, so you do not have to pick formats from a long menu or worry about selecting the wrong target.`,
      },
      {
        title: `Convert into ${toLabel}`,
        body: `SaveItPro processes the image into ${toLabel}, which is ${toMeta.conversionNote}. The output is prepared as a downloadable ${toExtensions} file.`,
      },
      {
        title: `Download and use the result`,
        body: `After conversion, download the ${toLabel} image and use it for ${toMeta.commonUses.join(', ')}, or keep the original ${fromLabel} file unchanged.`,
      },
    ],
    examples: [
      {
        title: `${fromLabel} ${firstUse(from)} to ${toLabel}`,
        body: `Turn ${fromLabel} files used for ${firstUse(from)} into ${toLabel} when a website, app, or publishing workflow requires ${toExtensions} output.`,
      },
      {
        title: `Prepare ${toLabel} assets for ${firstUse(to)}`,
        body: `Convert client-supplied ${fromLabel} artwork into ${toLabel} for ${toMeta.commonUses.join(', ')} without installing desktop image software.`,
      },
      {
        title: `Make a compatible ${toLabel} copy`,
        body: `Keep the original ${fromLabel} file for editing, then create a ${toLabel} copy for upload forms, CMS media libraries, design handoffs, or QA checks.`,
      },
    ],
    benefits: [
      {
        title: `Purpose-built ${conversionName} page`,
        body: `Every page is dedicated to one direction, so the instructions, examples, and download labels match ${fromLabel} input and ${toLabel} output.`,
      },
      {
        title: toMeta.strengths[0],
        body: `${toLabel} is often chosen for ${toMeta.strengths.join(', ')}, making it a practical target for ${toMeta.commonUses.join(', ')}.`,
      },
      {
        title: `No signup for quick conversions`,
        body: `Use the converter for one-off ${conversionName} jobs when you just need a clean output file and do not want to create an account.`,
      },
      {
        title: `Original file stays unchanged`,
        body: `The conversion creates a new ${toLabel} download, which helps preserve your source ${fromLabel} image for future edits or alternate exports.`,
      },
    ],
    troubleshooting: [
      {
        title: `${fromLabel} file will not upload`,
        body: `Check that the file extension is one of ${fromExtensions}. If the image came from a camera, scanner, or design app, exporting it again as ${fromLabel} can fix incomplete file headers.`,
      },
      {
        title: `${toLabel} output looks different`,
        body: `${fromMeta.watchOut} ${toMeta.watchOut} Format limits can affect transparency, color depth, animation, or file size during ${conversionName} conversion.`,
      },
      {
        title: `Download does not start`,
        body: `Wait until the progress reaches 100%, then try the download button again. Browser privacy extensions can also block generated file downloads.`,
      },
      {
        title: `Converted file is larger than expected`,
        body: `${toLabel} is optimized for ${toMeta.conversionNote}. If size matters more than fidelity, compare another output format such as WebP or JPEG.`,
      },
    ],
    faqs: [
      {
        title: `Is this ${conversionName} converter free?`,
        body: `Yes. You can convert ${fromLabel} images to ${toLabel} online without signing up.`,
      },
      {
        title: `What files can I upload for ${conversionName}?`,
        body: `Upload ${fromLabel} files using ${fromExtensions}. The converter creates a ${toLabel} output file such as ${toExtensions}.`,
      },
      {
        title: `Will ${conversionName} keep transparency?`,
        body:
          to === 'png' || to === 'webp'
            ? `${toLabel} can support transparency, but the source ${fromLabel} must include transparent pixels for them to appear in the result.`
            : `${toLabel} may not preserve transparent backgrounds. For transparent graphics, PNG or WebP is usually a better target.`,
      },
      {
        title: `Is ${toLabel} better than ${fromLabel}?`,
        body: `${toLabel} is not always better; it is different. Choose ${toLabel} when you need ${toMeta.strengths.join(', ')}, and keep ${fromLabel} when ${fromMeta.strengths.join(', ')} matter more.`,
      },
      {
        title: `Can I use the converted ${toLabel} file on my website?`,
        body: `Yes, as long as your website or CMS supports ${toExtensions}. ${toLabel} is commonly used for ${toMeta.commonUses.join(', ')}.`,
      },
      {
        title: `Does SaveItPro change my original ${fromLabel} file?`,
        body: `No. The tool creates a new ${toLabel} download and leaves the original ${fromLabel} file on your device unchanged.`,
      },
    ],
  };
}

export function getImageConverterStructuredData(
  from: ImageFormat,
  to: ImageFormat,
  path: string,
): object[] {
  const content = getImageSeoContent(from, to);
  const url = `${SITE_URL}${path}`;

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: content.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.title,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.body,
        },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: `How ${content.conversionName} conversion works`,
      description: `Step-by-step guide to convert ${content.conversionName} online with ${SITE_NAME}.`,
      totalTime: 'PT1M',
      url,
      step: content.howItWorks.map((step, index) => ({
        '@type': 'HowToStep',
        position: index + 1,
        name: step.title,
        text: step.body,
      })),
    },
  ];
}
